import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  buildRelayClipUrl,
  CLIP_AFTER_SEC,
  CLIP_BEFORE_SEC,
  LIVE_EDGE_MARGIN_MS,
  RELAY_PLAYLIST_AFTER_SEC,
  RELAY_PLAYLIST_BEFORE_SEC,
  rememberRelayRecordingFromPlaylist,
  resolveRelayProgramOffset,
} from "../utils/relayClip";

// El manifiesto cubre el rango completo del scrubber. Así hls.js mantiene el
// mismo MediaSource durante la reproducción y sólo descarga los fragmentos que
// vaya necesitando, sin reiniciar el vídeo cada pocos segundos.
const RELAY_PLAYLIST_LOAD_POLICY = {
  default: {
    maxTimeToFirstByteMs: 30_000,
    maxLoadTimeMs: 60_000,
    timeoutRetry: {
      maxNumRetry: 1,
      retryDelayMs: 500,
      maxRetryDelayMs: 1_000,
    },
    errorRetry: {
      maxNumRetry: 2,
      retryDelayMs: 1_000,
      maxRetryDelayMs: 3_000,
    },
  },
};
// Sólo entra en juego para VTRs que pueden reproducir fuera de la ventana del
// scrubber; la vista normal se detiene antes de llegar aquí.
const RELAY_PLAYLIST_RELOAD_MARGIN_SEC = 10;
const UI_POSITION_UPDATE_INTERVAL_MS = 500;

// Precarga el chunk de hls.js al cargar este módulo (junto con la vista) para
// no pagar el import dinámico al abrir el primer clip.
const hlsModulePromise = typeof window !== "undefined" ? import("hls.js") : null;

interface RelayWindowRequest {
  originAtMs: number;
  windowAtMs: number;
  targetWallMs: number;
  resumePlayback: boolean;
  seq: number;
}

// Handle imperativo que expone el control del <video> al player de arriba.
// La barra de controles (play/pausa, ±5s, velocidad, fullscreen) vive en el
// padre (StoryProducer); estos métodos la cablean al elemento real.
export interface RelayClipPlayerHandle {
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  seekBy: (deltaSec: number) => void;
  // Seek directo sobre currentTime + burst de audio. No comprueba rangos HLS.
  // Ideal para audio scrubbing frame a frame cuando el video está pausado.
  scrubBy: (deltaSec: number) => void;
  // Salta a una fracción [0..1] de la duración del clip (para la barra de seek).
  seekToFraction: (fraction: number) => void;
  // Salta a un wall-clock (UTC ms) DENTRO del clip ya cargado, sin recargar el HLS.
  // Devuelve true si el momento cae dentro del material cargado (seek fluido);
  // false si queda fuera de la ventana/duración (el caller debe recargar el clip).
  seekToWallClock: (wallMs: number) => boolean;
  setRate: (rate: number) => void;
  requestFullscreen: () => void;
  // Wall-clock (UTC, ms) de la posición actual del playhead, o null si aún no
  // hay mapeo PROGRAM-DATE-TIME ↔ currentTime.
  getWallClockMs: () => number | null;
}

export interface RelayClipPlayerProps {
  // Momento (wall-clock UTC) a reproducir. null = player ocioso (sin clip).
  at: Date | null;
  // Canal de video del relay ya resuelto (p.ej. "video-1"). Cambiarlo conmuta de
  // cámara conservando la posición (misma ventana ±30 min alrededor de `at`).
  channel: string;
  onPlayingChange?: (playing: boolean) => void;
  onLoadingChange?: (loading: boolean) => void;
  // Posición actual como wall-clock (UTC ms). Se emite en cada `timeupdate`.
  onWallClockChange?: (ms: number | null) => void;
  // Progreso de reproducción dentro del rango visual ±30 min alrededor del log.
  // La carga HLS real puede cubrir sólo una ventana corta de ese rango.
  onProgressChange?: (info: { fraction: number; ready: boolean }) => void;
  onError?: (msg: string | null) => void;
  // Wall-clock (UTC ms) del OUT del segmento. Al alcanzarlo se llama onReachedOut
  // una sola vez por clip — el VTR lo usa para encadenar el siguiente corte.
  // null = reproducir sin límite (hasta el final del clip).
  outWallMs?: number | null;
  onReachedOut?: () => void;
  // Si es false, carga y posiciona el clip pero NO llama play() automáticamente
  // (p.ej. para precargar un segmento con el VTR en pausa).
  autoPlayOnLoad?: boolean;
  // En players con scrubber acotado, el video se detiene al salir del rango
  // visual. El VTR program usa saltos por wall-clock dentro del HLS cargado,
  // así que necesita reproducir fuera de esa ventana.
  stopAtSeekWindowEnd?: boolean;
}

// Reproductor inline de un clip HLS histórico del relay. Es la lógica de carga
// del antiguo RelayClipModal, sin el chrome de modal: solo el <video> + overlays
// de loading/error. El padre dibuja los controles y los cablea vía el handle.
export const RelayClipPlayer = forwardRef<RelayClipPlayerHandle, RelayClipPlayerProps>(
  (
    {
      at,
      channel,
      onPlayingChange,
      onLoadingChange,
      onWallClockChange,
      onProgressChange,
      onError,
      outWallMs = null,
      onReachedOut,
      autoPlayOnLoad = true,
      stopAtSeekWindowEnd = true,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<import("hls.js").default | null>(null);
    const [windowRequest, setWindowRequest] = useState<RelayWindowRequest | null>(null);
    const pendingWindowReloadRef = useRef<{
      originAtMs: number;
      windowAtMs: number;
    } | null>(null);

    // Refs "latest" para leer estos props desde los listeners del <video> sin
    // re-disparar el efecto de carga HLS. reachedOutRef arma el OUT una vez por clip.
    const atMsRef = useRef<number | null>(null);
    // Clamp del objetivo al borde live, memorizado por cada `at` distinto (ver más
    // abajo dónde se computa `atMs`).
    const liveClampRef = useRef<{ raw: number; clamped: number } | null>(null);
    const outWallMsRef = useRef<number | null>(outWallMs);
    const onReachedOutRef = useRef(onReachedOut);
    const onPlayingChangeRef = useRef(onPlayingChange);
    const onLoadingChangeRef = useRef(onLoadingChange);
    const onWallClockChangeRef = useRef(onWallClockChange);
    const onProgressChangeRef = useRef(onProgressChange);
    const onErrorRef = useRef(onError);
    const autoPlayRef = useRef(autoPlayOnLoad);
    const stopAtSeekWindowEndRef = useRef(stopAtSeekWindowEnd);
    const reachedOutRef = useRef(false);
    // Refs para audio scrubbing frame-a-frame con debounce
    const scrubTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const scrubPauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const scrubWasPausedRef = useRef<boolean>(true);
    // El reproductor principal vive dentro de StoryProducer, un árbol muy
    // grande. El reloj y la barra son datos de UI, no de decodificación: limitar
    // sus updates evita volver a renderizar toda la vista por cada timeupdate.
    const lastUiPositionUpdateRef = useRef(0);
    const SCRUB_FPS = 29.97;
    // Borde live: un `at` que apunte a "ahora" cae en un instante aún no grabado
    // (el archivo llega hasta ~ahora − latencia) → el player esperaría ~esa
    // latencia a que exista. Clampeamos el objetivo al borde live disponible UNA
    // vez por cada `at` nuevo — nunca con `Date.now()` en cada render, que derivaría
    // y recargaría el clip en loop. Un `at` claramente pasado queda intacto (min()).
    const rawAtMs = at ? at.getTime() : null;
    if (rawAtMs == null) {
      liveClampRef.current = null;
    } else if (liveClampRef.current?.raw !== rawAtMs) {
      liveClampRef.current = {
        raw: rawAtMs,
        clamped: Math.min(rawAtMs, Date.now() - LIVE_EDGE_MARGIN_MS),
      };
    }
    const atMs = liveClampRef.current?.clamped ?? null;
    atMsRef.current = atMs;
    outWallMsRef.current = outWallMs ?? null;
    onReachedOutRef.current = onReachedOut;
    onPlayingChangeRef.current = onPlayingChange;
    onLoadingChangeRef.current = onLoadingChange;
    onWallClockChangeRef.current = onWallClockChange;
    onProgressChangeRef.current = onProgressChange;
    onErrorRef.current = onError;
    autoPlayRef.current = autoPlayOnLoad;
    stopAtSeekWindowEndRef.current = stopAtSeekWindowEnd;

    // Ancla para mapear currentTime ↔ wall-clock. Se fija al hacer el seek inicial:
    // sabemos que `offsetSec` (posición dentro del clip) corresponde a `wallMs`
    // (el momento del log). Asumimos reproducción continua, suficiente para IN/OUT.
    const anchorRef = useRef<{ offsetSec: number; wallMs: number } | null>(null);

    // Posición a restaurar al recargar el <video>. Como ambos canales cubren la
    // misma ventana (mismo `at`), al cambiar de feed conservamos el currentTime en
    // vez de reiniciar. `atMs` distingue cambio-de-canal (restaurar) de
    // cambio-de-instante (re-seek al momento).
    const resumeRef = useRef<{
      originAtMs: number;
      windowAtMs: number;
      time: number;
      wallMs: number | null;
    } | null>(null);

    const activeWindowRequest =
      atMs != null && windowRequest?.originAtMs === atMs ? windowRequest : null;
    const playlistAtMs = activeWindowRequest?.windowAtMs ?? atMs;
    const targetWallMs = activeWindowRequest?.targetWallMs ?? atMs;
    const src =
      playlistAtMs != null
        ? buildRelayClipUrl(new Date(playlistAtMs), channel, {
            beforeSec: RELAY_PLAYLIST_BEFORE_SEC,
            afterSec: RELAY_PLAYLIST_AFTER_SEC,
          })
        : null;

    const loadedWallRange = (): { startMs: number; endMs: number } | null => {
      const v = videoRef.current;
      const a = anchorRef.current;
      const dur = v && Number.isFinite(v.duration) ? v.duration : 0;
      if (!a || dur <= 0) return null;
      const startMs = a.wallMs - a.offsetSec * 1000;
      return { startMs, endMs: startMs + dur * 1000 };
    };

    const currentWallClockMs = (currentTime: number): number | null => {
      const a = anchorRef.current;
      if (!a) return null;
      return a.wallMs + (currentTime - a.offsetSec) * 1000;
    };

    const scrubWallRange = (): { startMs: number; endMs: number } | null => {
      const originAtMs = atMsRef.current;
      if (originAtMs == null) return null;
      // El extremo derecho del scrubber se acota al borde live: `at + CLIP_AFTER`
      // casi siempre cae en el futuro (aún no grabado). Sin este min() el seeker
      // dejaría arrastrar —o el auto-avance llegar— a un instante inexistente y
      // pedir `/api/play?at=<futuro>` → 404/cuelgue. La carga inicial ya se clampea
      // igual (ver `atMs`); esto cierra el mismo hueco para el scrubbing/playback.
      const liveEdgeMs = Date.now() - LIVE_EDGE_MARGIN_MS;
      const endMs = Math.min(originAtMs + CLIP_AFTER_SEC * 1000, liveEdgeMs);
      return {
        startMs: originAtMs - CLIP_BEFORE_SEC * 1000,
        // Salvaguarda: si `originAtMs` ya estuviera por delante del borde live
        // (no debería, va clampeado), mantené la ventana no-vacía.
        endMs: Math.max(endMs, originAtMs),
      };
    };

    const requestWindowAround = (
      wallMs: number,
      resumePlayback: boolean,
      force = false,
    ): boolean => {
      const originAtMs = atMsRef.current;
      if (originAtMs == null || !Number.isFinite(wallMs)) return false;
      const windowAtMs = Math.round(wallMs);
      if (!force && pendingWindowReloadRef.current?.originAtMs === originAtMs) return true;

      pendingWindowReloadRef.current = { originAtMs, windowAtMs };
      onLoadingChangeRef.current?.(true);
      setWindowRequest((prev) => ({
        originAtMs,
        windowAtMs,
        targetWallMs: wallMs,
        resumePlayback,
        seq: (prev?.seq ?? 0) + 1,
      }));
      return true;
    };

    const seekOrLoadWallClock = (
      wallMs: number,
      resumePlayback: boolean,
      forceReload = false,
    ): boolean => {
      const v = videoRef.current;
      const a = anchorRef.current;
      const dur = v && Number.isFinite(v.duration) ? v.duration : 0;
      const target = a ? a.offsetSec + (wallMs - a.wallMs) / 1000 : Number.NaN;
      if (!forceReload && v && a && dur > 0 && target >= 0 && target <= dur) {
        reachedOutRef.current = false;
        v.currentTime = target;
        emitWallClock(v.currentTime);
        emitProgress();
        return true;
      }

      return requestWindowAround(wallMs, resumePlayback, true);
    };

    const emitProgress = () => {
      const v = videoRef.current;
      const range = scrubWallRange();
      const wallMs = v ? currentWallClockMs(v.currentTime) : null;
      if (wallMs == null || !range) {
        onProgressChangeRef.current?.({ fraction: 0, ready: false });
        return;
      }
      const fraction = Math.max(
        0,
        Math.min((wallMs - range.startMs) / (range.endMs - range.startMs), 1),
      );
      onProgressChangeRef.current?.({ fraction, ready: true });
    };

    // Si el playhead está pegado al final del rango visual, reinícialo al inicio
    // antes de reproducir (evita que play() se quede "muerto" tras parar).
    const rewindIfAtWindowEnd = (v: HTMLVideoElement): boolean => {
      if (!stopAtSeekWindowEndRef.current) return false;
      const range = scrubWallRange();
      const wallMs = currentWallClockMs(v.currentTime);
      if (range && wallMs != null && wallMs >= range.endMs - 50) {
        return seekOrLoadWallClock(range.startMs, true, true);
      }
      return false;
    };

    const emitWallClock = (currentTime: number, notifyParent = true): number | null => {
      const ms = currentWallClockMs(currentTime);
      if (ms == null) {
        if (notifyParent) onWallClockChangeRef.current?.(null);
        return null;
      }
      if (notifyParent) onWallClockChangeRef.current?.(ms);
      const out = outWallMsRef.current;
      if (out != null && !reachedOutRef.current && ms >= out) {
        reachedOutRef.current = true;
        onReachedOutRef.current?.();
      }
      return ms;
    };

    useImperativeHandle(ref, () => ({
      play: () => {
        const v = videoRef.current;
        if (!v) return;
        if (rewindIfAtWindowEnd(v)) return;
        v.play().catch(() => {});
      },
      pause: () => videoRef.current?.pause(),
      togglePlay: () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) {
          if (rewindIfAtWindowEnd(v)) return;
          v.play().catch(() => {});
        } else v.pause();
      },
      seekBy: (deltaSec: number) => {
        const v = videoRef.current;
        if (!v) return;
        const wallMs = currentWallClockMs(v.currentTime);
        if (wallMs == null) return;
        const wasPaused = v.paused;
        const targetWallMs = wallMs + deltaSec * 1000;
        const ok = seekOrLoadWallClock(targetWallMs, !v.paused);

        // Audio Scrubbing ligero: play y pausa inmediata en cuanto se inicializa el stream
        if (ok && wasPaused && Math.abs(deltaSec) <= 0.1) {
          v.play()
            .then(() => {
              v.pause();
            })
            .catch(() => {});
        }
      },
      // Audio scrubbing frame-a-frame:
      // Seek preciso por frame index + burst de audio tras el seeked
      scrubBy: (deltaSec: number) => {
        const v = videoRef.current;
        if (!v) return;

        // Cancelar timers pendientes
        if (scrubTimerRef.current) {
          clearTimeout(scrubTimerRef.current);
          scrubTimerRef.current = null;
        }
        if (scrubPauseTimerRef.current) {
          clearTimeout(scrubPauseTimerRef.current);
          scrubPauseTimerRef.current = null;
        }

        // Recordar si estaba en play y pausar para el scrub
        if (!v.paused) {
          scrubWasPausedRef.current = false;
          v.pause();
        }

        // Seek ±1 frame (frame index para evitar acumulación de error float)
        const fps = 29.97;
        const frameStep = deltaSec > 0 ? 1 : -1;
        const targetFrame = Math.max(0, Math.round(v.currentTime * fps) + frameStep);
        v.currentTime = targetFrame / fps;

        // Burst de audio al terminar el seek: play 40ms y pausa
        const doBurst = () => {
          const vid = videoRef.current;
          if (!vid) return;
          vid.play().catch(() => {});
          scrubPauseTimerRef.current = setTimeout(() => {
            scrubPauseTimerRef.current = null;
            videoRef.current?.pause();
          }, 40);
        };

        if (v.seeking) {
          const onSeeked = () => {
            v.removeEventListener("seeked", onSeeked);
            doBurst();
          };
          v.addEventListener("seeked", onSeeked);
          scrubTimerRef.current = setTimeout(() => {
            scrubTimerRef.current = null;
            v.removeEventListener("seeked", onSeeked);
            doBurst();
          }, 300);
        } else {
          doBurst();
        }
      },
      seekToFraction: (fraction: number) => {
        const range = scrubWallRange();
        const v = videoRef.current;
        if (!range || !v) return;
        const f = Math.max(0, Math.min(fraction, 1));
        const targetWallMs = range.startMs + f * (range.endMs - range.startMs);
        seekOrLoadWallClock(targetWallMs, !v.paused);
      },
      seekToWallClock: (wallMs: number) => {
        const v = videoRef.current;
        const a = anchorRef.current;
        if (!v || !a) return false;
        // Anti-desfase: el nuevo corte reactiva el OUT del segmento.
        reachedOutRef.current = false;
        const target = a.offsetSec + (wallMs - a.wallMs) / 1000;
        const dur = Number.isFinite(v.duration) ? v.duration : 0;
        if (dur <= 0 || target < 0 || target > dur) return false;
        v.currentTime = target;
        emitWallClock(v.currentTime);
        emitProgress();
        return true;
      },
      setRate: (rate: number) => {
        if (videoRef.current) videoRef.current.playbackRate = rate;
      },
      requestFullscreen: () => {
        const v = videoRef.current as
          | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
          | null;
        if (!v) return;
        if (v.requestFullscreen) v.requestFullscreen().catch(() => {});
        else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
      },
      getWallClockMs: () => {
        const v = videoRef.current;
        if (!v) return null;
        return currentWallClockMs(v.currentTime);
      },
    }));

    // Refleja play/pausa y la posición del <video> hacia el padre.
    useEffect(() => {
      const v = videoRef.current;
      if (!v) return;
      const onPlay = () => onPlayingChangeRef.current?.(true);
      const onPause = () => onPlayingChangeRef.current?.(false);
      const onTime = () => {
        const now = performance.now();
        const notifyUi = now - lastUiPositionUpdateRef.current >= UI_POSITION_UPDATE_INTERVAL_MS;
        if (notifyUi) lastUiPositionUpdateRef.current = now;
        // La detección de OUT se ejecuta en cada timeupdate; sólo las
        // actualizaciones visuales se limitan a dos por segundo.
        const wallMs = emitWallClock(v.currentTime, notifyUi);
        if (notifyUi) emitProgress();

        const scrubRange = scrubWallRange();
        if (
          stopAtSeekWindowEndRef.current &&
          scrubRange &&
          wallMs != null &&
          wallMs >= scrubRange.endMs
        ) {
          v.pause();
          return;
        }

        const loadedRange = loadedWallRange();
        if (
          !v.paused &&
          loadedRange &&
          wallMs != null &&
          wallMs >= loadedRange.endMs - RELAY_PLAYLIST_RELOAD_MARGIN_SEC * 1000
        ) {
          requestWindowAround(wallMs, true);
        }
      };
      v.addEventListener("play", onPlay);
      v.addEventListener("pause", onPause);
      v.addEventListener("timeupdate", onTime);
      return () => {
        v.removeEventListener("play", onPlay);
        v.removeEventListener("pause", onPause);
        v.removeEventListener("timeupdate", onTime);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const video = videoRef.current;
      if (!video || !src || atMs == null || playlistAtMs == null || targetWallMs == null) return;

      let hls: import("hls.js").default | null = null;
      let cancelled = false;
      const shouldPlayOnLoad = activeWindowRequest?.resumePlayback ?? autoPlayRef.current;
      onErrorRef.current?.(null);
      onLoadingChangeRef.current?.(true);
      // Nuevo clip → re-arma el OUT del segmento.
      reachedOutRef.current = false;

      const fail = (msg: string) => {
        if (!cancelled) {
          pendingWindowReloadRef.current = null;
          onErrorRef.current?.(msg);
          onLoadingChangeRef.current?.(false);
        }
      };

      // Coloca el playhead una sola vez por carga. Al cambiar de feed se conserva
      // el wall-clock, pero cada playlist calcula su propio offset: las cámaras
      // pueden comenzar a grabar en segmentos distintos y su currentTime no es
      // intercambiable.
      let seeked = false;
      const applyInitialSeek = (momentOffsetSec: number, momentWallMs: number) => {
        if (seeked) return;
        seeked = true;
        // `momentOffsetSec` ya se resolvió contra PROGRAM-DATE-TIME del playlist
        // recién cargado. No se puede reutilizar `r.time`: es un offset relativo
        // al playlist/canal anterior y hacía que Stream 1 arrancara desfasado al
        // volver desde Stream 2.
        const target = momentOffsetSec;
        const anchorWallMs = momentWallMs;
        // Fija el ancla currentTime↔wall-clock con el offset real del objetivo.
        anchorRef.current = { offsetSec: target, wallMs: anchorWallMs };
        pendingWindowReloadRef.current = null;
        if (target > 0) {
          try {
            video.currentTime = target;
          } catch {
            /* algunos navegadores rechazan el seek hasta tener buffer; no es fatal */
          }
        }
        lastUiPositionUpdateRef.current = performance.now();
        emitWallClock(video.currentTime);
        emitProgress();
      };

      let usingHls = false;

      // Cada URL se centra en `playlistAtMs`, de modo que el objetivo siempre
      // cae aproximadamente a `BEFORE_SEC`. No reutilizamos el currentTime del
      // canal previo: puede tener un comienzo distinto aunque represente la
      // misma hora real.
      const expectedStartSec = RELAY_PLAYLIST_BEFORE_SEC;

      (hlsModulePromise ?? import("hls.js"))
        .then(({ default: Hls }) => {
          if (cancelled) return;

          if (!Hls.isSupported()) {
            // Safari/iOS: HLS nativo. PDT no es accesible fácil → aproximamos el
            // objetivo al offset esperado dentro de la ventana corta.
            if (video.canPlayType("application/vnd.apple.mpegurl")) {
              video.src = src;
              video.addEventListener(
                "loadedmetadata",
                () => {
                  if (cancelled) return;
                  const dur = Number.isFinite(video.duration)
                    ? video.duration
                    : RELAY_PLAYLIST_BEFORE_SEC;
                  applyInitialSeek(Math.min(RELAY_PLAYLIST_BEFORE_SEC, dur), targetWallMs);
                  if (shouldPlayOnLoad) video.play().catch(() => {});
                  onLoadingChangeRef.current?.(false);
                },
                { once: true },
              );
              video.addEventListener(
                "error",
                () =>
                  fail(
                    t("storyproducer.relayClip.playbackError", "No se pudo reproducir el clip."),
                  ),
                { once: true },
              );
            } else {
              fail(
                t(
                  "storyproducer.relayClip.unsupported",
                  "Este navegador no soporta la reproducción HLS.",
                ),
              );
            }
            return;
          }

          usingHls = true;
          hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false,
            startFragPrefetch: true,
            startPosition: expectedStartSec,
            manifestLoadingTimeOut: 30_000,
            levelLoadingTimeOut: 30_000,
            manifestLoadPolicy: RELAY_PLAYLIST_LOAD_POLICY,
            playlistLoadPolicy: RELAY_PLAYLIST_LOAD_POLICY,
            xhrSetup: (xhr, requestUrl) => {
              xhr.addEventListener("load", () => {
                if (cancelled) return;
                let responseText: string | null = null;
                try {
                  responseText = typeof xhr.responseText === "string" ? xhr.responseText : null;
                } catch {
                  responseText = null;
                }
                rememberRelayRecordingFromPlaylist(requestUrl, responseText);
              });
            },
          });
          hlsRef.current = hls;
          hls.loadSource(src);
          hls.attachMedia(video);

          // Al cargar el media playlist conocemos los PROGRAM-DATE-TIME reales:
          // ubicamos el playhead en el fragmento que contiene el momento.
          hls.on(Hls.Events.LEVEL_LOADED, (_e, data) => {
            if (cancelled) return;
            const frags = data.details?.fragments || [];
            const r = resumeRef.current;
            const resumeWallMs =
              r &&
              r.originAtMs === atMs &&
              r.windowAtMs === playlistAtMs &&
              r.wallMs != null &&
              r.time > 0
                ? r.wallMs
                : null;
            const initialWallMs = resumeWallMs ?? targetWallMs;
            const offset = resolveRelayProgramOffset(
              frags,
              initialWallMs,
              data.details?.totalduration,
              RELAY_PLAYLIST_BEFORE_SEC,
            );
            applyInitialSeek(offset, initialWallMs);
            if (shouldPlayOnLoad) video.play().catch(() => {});
          });

          // Re-ancla currentTime↔wall-clock desde el fragmento que hls.js está
          // reproduciendo REALMENTE: su programDateTime (PDT real del contenido) y
          // su `start` (posición en currentTime). El ancla de applyInitialSeek es
          // una estimación —el seek puede no caer exactamente donde se pidió (p.ej.
          // por startPosition o el snapping a keyframe)— y sin re-anclar ese error
          // PERSISTE: el reloj del player queda corrido respecto del frame mostrado.
          // Esto lo mantiene exacto cuadro a cuadro y auto-corrige tras cada seek.
          hls.on(Hls.Events.FRAG_CHANGED, (_e, data) => {
            if (cancelled) return;
            const frag = data?.frag;
            const pdt = frag?.programDateTime;
            if (typeof pdt !== "number" || !Number.isFinite(pdt) || !Number.isFinite(frag?.start)) {
              return;
            }
            anchorRef.current = { offsetSec: frag.start, wallMs: pdt };
          });

          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            if (!cancelled) onLoadingChangeRef.current?.(false);
          });
          hls.on(Hls.Events.ERROR, (_evt, data) => {
            if (!data.fatal) return;
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
              if (data.response?.code === 404) {
                fail(
                  t("storyproducer.relayClip.notFound", "No hay video grabado para este instante."),
                );
              } else {
                fail(
                  t(
                    "storyproducer.relayClip.networkError",
                    "Error al cargar el clip (¿CORS o red?).",
                  ),
                );
              }
            } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
              hls?.recoverMediaError();
            } else {
              fail(t("storyproducer.relayClip.playbackError", "No se pudo reproducir el clip."));
            }
          });
        })
        .catch(() =>
          fail(t("storyproducer.relayClip.loadFailed", "No se pudo cargar el reproductor.")),
        );

      return () => {
        cancelled = true;
        // Recordamos dónde íbamos para reanudar tras un cambio de feed.
        resumeRef.current = {
          originAtMs: atMs,
          windowAtMs: playlistAtMs,
          time: video.currentTime,
          wallMs: currentWallClockMs(video.currentTime),
        };
        if (hls) {
          hls.destroy();
          hls = null;
        }
        hlsRef.current = null;
        if (!usingHls) {
          video.removeAttribute("src");
          video.load();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src, atMs, playlistAtMs, targetWallMs, activeWindowRequest?.seq, t]);

    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        ref={videoRef}
        playsInline
        preload="auto"
        // contain: respeta el aspecto real del video (no estira).
        style={{
          width: "100%",
          height: "100%",
          background: "#000",
          objectFit: "contain",
        }}
      />
    );
  },
);

RelayClipPlayer.displayName = "RelayClipPlayer";

export default RelayClipPlayer;
